// Form Protection Utilities
// Multi-layered approach to prevent fraud, bots, and abuse

export interface FormProtectionData {
  // Honeypot field (invisible to users, should remain empty)
  honeypot: string;
  
  // Timing validation
  formStartTime: number;
  submitTime: number;
  
  // Browser fingerprinting (basic)
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
}

export interface ProtectionValidationResult {
  isValid: boolean;
  errors: string[];
  riskScore: number; // 0-100, higher = more suspicious
}

// Generate a simple math challenge (DEPRECATED - Math CAPTCHA removed)
export function generateMathChallenge(): { question: string; answer: number } {
  // Math CAPTCHA has been removed - this function is kept for compatibility
  return { question: '', answer: 0 };
}

// Initialize form protection
export function initializeFormProtection(): FormProtectionData {
  return {
    honeypot: '',
    formStartTime: Date.now(),
    submitTime: 0,
    userAgent: navigator.userAgent || '',
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    language: navigator.language || 'en'
  };
}

// Refresh math challenge (DEPRECATED - Math CAPTCHA removed)
export function refreshMathChallenge(): { question: string; answer: number } {
  // Math CAPTCHA has been removed - this function is kept for compatibility
  return { question: '', answer: 0 };
}

// Validate form protection on client side
export function validateFormProtection(data: FormProtectionData): ProtectionValidationResult {
  const errors: string[] = [];
  let riskScore = 0;
  
  // Check honeypot (should be empty)
  if (data.honeypot && data.honeypot.trim() !== '') {
    errors.push('Security validation failed');
    riskScore += 50; // High risk - likely bot
  }
  
  // Check timing (minimum 1 second to fill form - very lenient)
  const timeTaken = Date.now() - data.formStartTime;
  if (timeTaken < 1000) { // Less than 1 second - only catch very obvious bots
    errors.push('Please take your time to fill out the form');
    riskScore += 30;
  }
  
  // Check for basic bot patterns
  if (!data.userAgent || data.userAgent.includes('bot') || data.userAgent.includes('crawler')) {
    riskScore += 40;
  }
  
  // Check for suspicious screen resolution
  if (data.screenResolution === '0x0' || !data.screenResolution.includes('x')) {
    riskScore += 15;
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    riskScore
  };
}

// Generate a unique session ID for tracking
export function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Detect if user is likely using automation tools
export function detectAutomation(): boolean {
  // Check for common automation indicators
  const indicators = [
    // Selenium
    (window as any).webdriver !== undefined,
    (window as any).__webdriver_evaluate !== undefined,
    (window as any).__selenium_evaluate !== undefined,
    (window as any).__webdriver_script_function !== undefined,
    (window as any).__webdriver_script_func !== undefined,
    (window as any).__webdriver_script_fn !== undefined,
    (window as any).__fxdriver_evaluate !== undefined,
    (window as any).__driver_unwrapped !== undefined,
    (window as any).__webdriver_unwrapped !== undefined,
    (window as any).__driver_evaluate !== undefined,
    (window as any).__fxdriver_unwrapped !== undefined,
    (window as any).__webdriver_evaluate !== undefined,
    
    // Puppeteer
    (window as any)._phantom !== undefined,
    (window as any).__phantom !== undefined,
    (window as any).callPhantom !== undefined,
    
    // PhantomJS
    (window as any).phantom !== undefined,
    
    // Headless Chrome
    navigator.webdriver === true,
    
    // Other indicators
    navigator.plugins?.length === 0,
    navigator.languages?.length === 0
  ];
  
  return indicators.some(indicator => indicator);
}

// Enhanced form protection with automation detection
export function validateFormProtectionEnhanced(data: FormProtectionData): ProtectionValidationResult {
  const basicValidation = validateFormProtection(data);
  
  // Add automation detection (only for obvious automation tools)
  if (detectAutomation()) {
    // Check if it's obvious automation (webdriver, selenium, etc.)
    const isObviousAutomation = 
      (window as any).webdriver !== undefined ||
      (window as any).__webdriver_evaluate !== undefined ||
      (window as any).__selenium_evaluate !== undefined ||
      navigator.webdriver === true;
    
    if (isObviousAutomation) {
      basicValidation.errors.push('Automated form submission detected');
      basicValidation.riskScore += 60;
      basicValidation.isValid = false;
    } else {
      // For other automation indicators, just increase risk score without blocking
      basicValidation.riskScore += 20;
    }
  }
  
  // Check for suspicious timing patterns
  const timeTaken = Date.now() - data.formStartTime;
  if (timeTaken > 30 * 60 * 1000) { // More than 30 minutes
    basicValidation.riskScore += 10; // Slightly suspicious but not blocking
  }
  
  return basicValidation;
}

// Create form protection middleware data for server validation
export function createProtectionPayload(data: FormProtectionData): any {
  return {
    ...data,
    submitTime: Date.now(),
    sessionId: generateSessionId(),
    automationDetected: detectAutomation(),
    timeTakenMs: Date.now() - data.formStartTime
  };
}